import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import EditIcon from '@mui/icons-material/Edit';
import React, { useEffect, useRef } from 'react';

import { IOnlinePublicationMessages } from '../../../../types';
import { styles } from './InlineEditText.styles';

export type IInlineEditTextProps = {
  messages: IOnlinePublicationMessages;
  buttonDisabled?: boolean;
  maxCharacterCount?: number;
  text: string;
  saveAction(value: string): void;
};

export const InlineEditText: React.FC<IInlineEditTextProps> = ({
  buttonDisabled = false,
  messages,
  maxCharacterCount = 60,
  text,
  saveAction
}) => {
  const [editingValue, setEditingValue] = React.useState(text);
  const [isEditMode, setIsEditMode] = React.useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (inputRef?.current) inputRef.current.focus();
  }, [isEditMode]);

  useEffect(() => {
    setEditingValue(text);
  }, [text]);

  const onKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter' || event.key === 'Escape') {
      (event.target as HTMLElement).blur();
    }
  };

  const onBlur = (value: string) => {
    setIsEditMode(false);
    if (value.trim() === '') {
      setEditingValue(text);
    } else {
      saveAction(value);
    }
  };

  return (
    <Box data-test="edit-inline-view" sx={styles.inputArea}>
      {!isEditMode ? (
        <Box sx={styles.titleView}>
          <Typography variant="subtitle1" sx={styles.headerText}>
            {text}
          </Typography>
          <Button
            disabled={buttonDisabled}
            size="small"
            variant="text"
            aria-label="edit"
            data-test="edit-menu-button"
            sx={styles.editButton}
            onClick={() => setIsEditMode(true)}
            startIcon={<EditIcon fontSize="small" />}
          >
            Edit
          </Button>
        </Box>
      ) : (
        <Box sx={styles.editMenuContainer}>
          <TextField
            inputRef={inputRef}
            inputProps={{ maxLength: maxCharacterCount, 'data-test': 'edit-name-input', className: 'no-format' }}
            size="small"
            placeholder={messages.enterName}
            value={editingValue}
            onChange={(e) => setEditingValue(e.target.value)}
            onKeyDown={onKeyDown}
            onBlur={(e) => onBlur(e.target.value)}
            sx={styles.input}
          />
          <Typography variant="caption" sx={styles.charCounter}>
            {editingValue?.length ?? 0}/{maxCharacterCount}
          </Typography>
        </Box>
      )}
    </Box>
  );
};
